import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard, SecondaryButton } from "@/components/ui";
import {
  actionAuditEventIsExpired,
  actionAuditReferenceDate,
  actionAuditValueDisplayString,
  isActionAuditFieldUndoable,
  type ActionAuditEvent,
  type ActionAuditFieldChange,
} from "@/models/actionAudit";
import { actionAuditScopes } from "@/models/featureAccessPolicy";
import { activeMembership, resolvedLeagueID } from "@/models/user";
import {
  canUndoActionAuditEvent,
  eventMatchesActionAuditScopes,
  loadActionAuditEvent,
  loadActionAuditEvents,
  undoActionAuditField,
} from "@/services/actionAudit";
import {
  actionAuditActionLabel,
  actionAuditCollectionDisplayName,
  actionAuditFieldName,
} from "./activityHelpers";
import "./activity.css";

function formatDate(date: Date): string {
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function linkedUndoKeys(events: ActionAuditEvent[], eventID: string): Set<string> {
  return new Set(
    events
      .filter((candidate) => candidate.actionType === "undo" && candidate.reversesEventID === eventID)
      .flatMap((candidate) => candidate.reversesFieldKey ? [candidate.reversesFieldKey] : []),
  );
}

export function ActivityDetailView() {
  const { eventId } = useParams<{ eventId: string }>();
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const membership = currentUser ? activeMembership(currentUser) : undefined;
  const leagueID =
    currentUser?.activeContext?.leagueID
    ?? (currentUser?.activeContext?.targetType === "league" ? currentUser.activeContext.targetID : undefined)
    ?? (membership ? resolvedLeagueID(membership) : undefined);
  const scopes = useMemo(
    () => actionAuditScopes(currentUser, isSBotLabsAdmin, leagueID),
    [currentUser, isSBotLabsAdmin, leagueID],
  );
  const [event, setEvent] = useState<ActionAuditEvent | null>(null);
  const [undoneKeys, setUndoneKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [undoingKey, setUndoingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!eventId || !scopes.length) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    setEvent(null);
    setUndoneKeys(new Set());
    try {
      const [loadedEvent, visibleEvents] = await Promise.all([
        loadActionAuditEvent(eventId),
        loadActionAuditEvents(scopes),
      ]);
      if (!loadedEvent || !eventMatchesActionAuditScopes(loadedEvent, scopes)) {
        setEvent(null);
        setNotFound(true);
        return;
      }
      setEvent(loadedEvent);
      setUndoneKeys(linkedUndoKeys(visibleEvents, loadedEvent.id));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Change details could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [eventId, scopes]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const undo = async (change: ActionAuditFieldChange) => {
    if (!event || !currentUser) return;
    const previousValue = actionAuditValueDisplayString(change.oldValue);
    if (!window.confirm(`Restore ${actionAuditFieldName(change)} to ${previousValue}?`)) return;

    setUndoingKey(change.key);
    setError(null);
    try {
      await undoActionAuditField({
        event,
        change,
        currentUser,
        isSBotLabsAdmin,
      });
      setUndoneKeys((current) => new Set(current).add(change.key));
    } catch (undoError) {
      setError(undoError instanceof Error ? undoError.message : "This field could not be restored.");
    } finally {
      setUndoingKey(null);
    }
  };

  if (loading && !event) return <LoadingView label="Loading change details…" />;
  if (event && !eventMatchesActionAuditScopes(event, scopes)) {
    return <LoadingView label="Loading change details…" />;
  }
  if (notFound) {
    return (
      <SBCard className="activity-detail-state">
        <h1>Change unavailable</h1>
        <p>This activity no longer exists, has expired, or isn’t available in your current workspace.</p>
        <Link to="/home/activity">Back to Recent Activity</Link>
      </SBCard>
    );
  }
  if (!event) {
    return (
      <SBCard className="activity-detail-state">
        <h1>Couldn’t load this change</h1>
        <ErrorText>{error}</ErrorText>
        <div className="activity-detail-retry">
          <SecondaryButton title="Try again" onClick={() => void loadDetail()} />
        </div>
      </SBCard>
    );
  }

  const expired = actionAuditEventIsExpired(event);
  const canUndoEvent = canUndoActionAuditEvent(event, currentUser, isSBotLabsAdmin);
  const actorLine = event.actorKind === "aiAssistant"
    ? `SBot AI on behalf of ${event.actorDisplayName}${event.toolActionType ? ` (${event.toolActionType})` : ""}`
    : event.actorKind === "externalAgent"
      ? `External agent on behalf of ${event.actorDisplayName}${event.toolActionType ? ` (${event.toolActionType})` : ""}`
      : event.actorDisplayName;

  return (
    <div className="activity-screen activity-detail">
      <Link className="activity-back-link" to="/home/activity">← Recent Activity</Link>

      <SBCard className="activity-metadata">
        <div className="activity-metadata-head">
          <div>
            <h1>{event.targetDisplayName.trim() || event.targetCollection}</h1>
            <p>{actionAuditCollectionDisplayName(event.targetCollection)}</p>
          </div>
          <span className={`badge badge-activity-action badge-activity-${event.actionType}`}>
            {actionAuditActionLabel(event.actionType)}
          </span>
        </div>

        <dl className="activity-metadata-list">
          <div><dt>By</dt><dd>{actorLine}</dd></div>
          <div><dt>When</dt><dd>{formatDate(actionAuditReferenceDate(event))}</dd></div>
          <div>
            <dt>Undo</dt>
            <dd>{expired ? "Undo window closed" : `Available until ${formatDate(event.expiresAt)}`}</dd>
          </div>
        </dl>
        <ErrorText>{error}</ErrorText>
      </SBCard>

      {event.actionType === "undo" && (
        <SBCard className="activity-undo-banner">↶ This change reversed an earlier edit.</SBCard>
      )}

      {event.fieldChanges.length > 0 && (
        <section className="activity-group">
          <h2>Changed fields</h2>
          <SBCard className="activity-changes-card">
            {event.fieldChanges.map((change) => {
              const isOverflow = change.key === "_truncatedChangeCount";
              const isUndone = undoneKeys.has(change.key);
              const canUndo = event.actionType !== "undo"
                && canUndoEvent
                && !expired
                && !isUndone
                && isActionAuditFieldUndoable(change);

              return (
                <div className="activity-change-row" key={change.key}>
                  <div className="activity-change-head">
                    <strong>{actionAuditFieldName(change)}</strong>
                    {isUndone && <span className="badge">Undone</span>}
                  </div>

                  {isOverflow ? (
                    <p>{actionAuditValueDisplayString(change.newValue)} additional changes were not recorded in detail.</p>
                  ) : (
                    <>
                      <div className="activity-value-change">
                        <span className="activity-old-value">{actionAuditValueDisplayString(change.oldValue)}</span>
                        <span aria-hidden="true">→</span>
                        <strong>{actionAuditValueDisplayString(change.newValue)}</strong>
                      </div>
                      {change.isTruncated && <p>Value shortened for storage — undo unavailable.</p>}
                      {canUndo && (
                        <div className="activity-undo-control">
                          <SecondaryButton
                            title={undoingKey === change.key ? "Restoring…" : "Undo"}
                            onClick={() => void undo(change)}
                            disabled={undoingKey !== null}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </SBCard>
        </section>
      )}
    </div>
  );
}
